import { memo, useContext, useDeferredValue, useState } from 'react';
import { SearchField, SidePane, Modal, TooltipStateLess } from '@library';
import type { SearchResultProduct } from '../../../../queries/useProductSearch';
import useProductSearch from '../../../../queries/useProductSearch';
import { AppContext } from '../../../../../core/context';
import withErrorBoundary from '../../../../../core/components/errors/withErrorBoundary';
import styles from './SkuProductSearch.module.scss';
import SkuProductSearchContent from './SkuProductSearchContent';
import SkuProductSearchProductDetails from './SkuProductSearchProductDetails';

const SkuProductSearch = ({ skuMask }: { skuMask: string }) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(skuMask);
    const searchValue = useDeferredValue(value);

    const { isInMobileView } = useContext(AppContext);

    const [openedProduct, setOpenedProduct] = useState<
        SearchResultProduct | undefined
    >(undefined);
    const [productDetailsOpen, openProductDetails] = useState(false);

    const { products, loading, error, total } = useProductSearch({
        search: searchValue,
        skip: searchValue.length < 3,
    });

    if (error) {
        throw error;
    }

    return (
        <div className={styles.container}>
            <TooltipStateLess
                open={open}
                rounded
                placement="bottom"
                fallbackPlacements={[]}
                dismiss={!productDetailsOpen}
                mode="click"
                onOpenChange={tooltipOpen => {
                    setOpen(tooltipOpen);
                }}
                content={
                    <div className={styles.tooltip}>
                        <SkuProductSearchContent
                            loading={loading}
                            products={products}
                            total={total}
                            searchTerm={searchValue}
                            openProduct={product => {
                                setOpenedProduct(product);
                                openProductDetails(true);
                            }}
                        />
                    </div>
                }
                theme={styles.theme}
            >
                <SearchField
                    light
                    value={value}
                    onChange={search => {
                        setOpen(true);
                        setValue(search);
                    }}
                    data-test-class="searchProduct"
                />
            </TooltipStateLess>

            <SidePane
                side="bottom"
                open={productDetailsOpen && isInMobileView}
                onOpenChange={change => {
                    if (!change) {
                        openProductDetails(false);
                    }
                }}
            >
                {openedProduct && (
                    <SkuProductSearchProductDetails
                        product={openedProduct}
                        hideEmptyContent
                    />
                )}
            </SidePane>

            <Modal
                open={productDetailsOpen && !isInMobileView}
                onOpenChange={change => {
                    if (!change) {
                        openProductDetails(false);
                    }
                }}
            >
                {openedProduct && (
                    <SkuProductSearchProductDetails product={openedProduct} />
                )}
            </Modal>
        </div>
    );
};

export default memo(withErrorBoundary(SkuProductSearch));
